
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { User, Phone, Calendar, Wrench } from 'lucide-react';
import BookingActionButtons from './BookingActionButtons';

interface BookingRecord {
  id: number;
  "Full Name": string;
  "Phone Number": number;
  "Date": string;
  "Vehicle Make": string;
  "Vehicle Model": string;
  "Oil Brand": string;
  "Oil Viscosity": string;
  "Liters": number;
  "Notes": string;
  "Payment Method": string;
  "Payment Status": string;
  "Shop ID": number;
  "Shop Name": string;
  booking_status: string;
  amount: number;
  created_at: string;
}

interface BookingTableProps {
  bookings: BookingRecord[];
  loading: boolean;
  onBookingUpdate: () => void;
}

const BookingTable = ({ bookings, loading, onBookingUpdate }: BookingTableProps) => {
  const getPaymentStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getBookingStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-autocare-blue"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (bookings.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8 text-gray-500">
            <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No bookings found</p>
            <p className="text-sm">Bookings will appear here when customers book services</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bookings ({bookings.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {bookings.map((booking) => (
            <div
              key={booking.id}
              className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <User className="h-4 w-4 text-autocare-blue" />
                    <span className="font-semibold text-lg">{booking["Full Name"]}</span>
                    <Badge className={`${getPaymentStatusColor(booking["Payment Status"])} font-medium`}>
                      Payment: {booking["Payment Status"] || 'pending'}
                    </Badge>
                    <Badge className={`${getBookingStatusColor(booking.booking_status)} font-medium`}>
                      Status: {booking.booking_status || 'pending'}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-2">
                      <Phone className="h-3 w-3" />
                      {booking["Phone Number"]}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3 w-3" />
                      {booking["Date"] ? format(new Date(booking["Date"]), 'MMM dd, yyyy') : 'No date'}
                    </div>
                    <div className="flex items-center gap-2">
                      <Wrench className="h-3 w-3" />
                      {booking["Vehicle Make"]} {booking["Vehicle Model"]}
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><span className="font-medium">Shop:</span> {booking["Shop Name"]}</p>
                    <p><span className="font-medium">Oil:</span> {booking["Oil Brand"]} {booking["Oil Viscosity"]} ({booking["Liters"]}L)</p>
                    <p><span className="font-medium">Payment Method:</span> {booking["Payment Method"]} | <span className="font-medium">Amount:</span> <span className="text-green-600 font-bold">${booking.amount?.toFixed(2) || '0.00'}</span></p>
                  </div>
                  
                  {booking["Notes"] && (
                    <p className="text-sm text-gray-600 mt-2 italic bg-gray-50 p-2 rounded">
                      "{booking["Notes"]}"
                    </p>
                  )}
                  
                  <p className="text-xs text-gray-400 mt-2">
                    Ordered: {format(new Date(booking.created_at), 'MMM dd, yyyy HH:mm')}
                  </p>
                </div>
                
                <div className="lg:w-56 flex justify-end">
                  <BookingActionButtons
                    bookingId={booking.id}
                    currentBookingStatus={booking.booking_status || 'pending'}
                    currentPaymentStatus={booking["Payment Status"] || 'pending'}
                    onUpdate={onBookingUpdate}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingTable;
